import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { authStore, fortuneWheelStore } from "../stores";
import { getApiUrl } from "../utils/apiUrl";
import { IUnusedSpin, IWheelSpinCreationEvent } from "../interfaces/daily-wheel";

class UserSessionService {
  private connection: HubConnection | null = null;

  connect = (gameId?: number, partnerId?: number, onSessionReplaced?: (internalSessionId: string) => void) => {
    const accessToken = authStore.accessToken;

    if (!accessToken) {
      console.error("Access token is missing");
      return;
    }

    const params = new URLSearchParams({ sessionId: this.makeId(30) });
    if (gameId != null) params.set("gameId", String(gameId));
    if (partnerId != null) params.set("partnerId", String(partnerId));

    this.connection = new HubConnectionBuilder()
      .withUrl(`${getApiUrl(process.env.REACT_APP_USER_SESSION_TRACKER_URL!)}/hubs/user-session?${params}`, {
        accessTokenFactory: () => accessToken,
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect([0, 1000, 2000, 3000, 5000, 10000, 10000])
      .build();

    // Sent to all of the player's launchers whenever one of them starts a new session (a game launched
    // elsewhere); each launcher compares the id with its own.
    this.connection.on("SessionReplaced", (event: { internalSessionId: string }) => {
      onSessionReplaced?.(event.internalSessionId);
    });

    // Listen for WheelSpinCreation events
    this.connection.on("WheelSpinCreation", (event: IWheelSpinCreationEvent) => {
      this.handleWheelSpinCreation(event);
    });

    this.connection
      .start()
      .then(() => {
        console.log("Connected to SignalR");
      })
      .catch((error) => {
        console.error("Error connecting to SignalR:", error);
      });
  };

  disconnect = () => {
    this.connection?.stop();
    this.connection = null;
  };

  makeId = (length: number) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  private handleWheelSpinCreation = (event: IWheelSpinCreationEvent) => {
    // Only add the spin if it's for the current user
    if (!authStore.userId || event.userId !== authStore.userId) {
      return;
    }

    const currentWheel = fortuneWheelStore.wheelData;
    // Only process if we have wheel data and the event is for the current wheel
    if (!currentWheel || event.wheelId !== currentWheel.id) {
      return;
    }

    // Convert the event to IUnusedSpin format and add to unused spins
    const newSpin: IUnusedSpin = {
      id: event.id,
      wheelId: event.wheelId,
      createdAt: event.createdAt,
      status: event.status,
    };

    // Add the new spin to the unused spins array if it's not already there
    const existingSpins = fortuneWheelStore.unusedSpins;
    const spinExists = existingSpins.some(spin => spin.id === newSpin.id);

    if (!spinExists) {
      fortuneWheelStore.setUnusedSpins([...existingSpins, newSpin]);
      // Trigger bounce animation to notify user of new spin
      fortuneWheelStore.triggerBounce();
    }
  };

}

export const userSessionService = new UserSessionService();